/** @jsx isolateComponent */
import { ReactNode } from 'react';

import { Animation, Colors } from '@zapier/design-system';
import type { ShadowValue } from '@zapier/design-system';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

type ColorName = keyof typeof Colors;
type ServiceIconShellProps = {
  /**
   * The width to render the ServiceIconShell component as.
   */
  width: number;
  /**
   * The height to render the ServiceIconShell component as.
   */
  height: number;
  /**
   * If the ServiceIconShell is "empty", which will cause the background to be grey.
   *
   * @default false
   */
  isEmpty?: boolean;
  /**
   * Whether to render the ServiceIconShell as part of a grouping of Icons.
   *
   * @default false
   */
  isInGroup?: boolean;
  /**
   * Applies a shadow to the ServiceIconShell.
   */
  shadow?: ShadowValue;
  /**
   * Applies a border color to the ServiceIconShell.
   *
   * @default 'grey200'
   */
  borderColor?: ColorName;
  /**
   * Applies a border size to the ServiceIconShell.
   *
   * @default 1
   */
  borderWidth?: number;
  /**
   * The children to render inside of the ServiceIconShell component.
   */
  children?: ReactNode;
};

const getPropsWithDefaults = (props: ServiceIconShellProps) => ({
  borderColor: 'grey200' as ColorName,
  borderWidth: 1,
  isInGroup: false,
  isEmpty: false,
  ...props,
});

const Styles = labelStyles('ServiceIconShell', {
  root: (_props: ServiceIconShellProps) => {
    const props = getPropsWithDefaults(_props);
    return [
      {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: Colors.white,
        border: `1px solid transparent`,
        borderRadius: Math.ceil(0.25 * props.height),
      },
      withModifier(`border-${String(props.borderColor)}`, {
        borderColor: Colors[props.borderColor],
      }),
      withModifier(`border-width-${props.borderWidth}`, {
        borderWidth: props.borderWidth,
      }),
      withModifier(`${props.width}x${props.height}`, {
        height: props.height,
        width: props.width,
      }),
      props.isInGroup &&
        withModifier('withinGroup', {
          '&:first-of-type:not(:only-child)': {
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            marginRight: -1, // Pulls next one over to overlap
          },
          '&:last-child:not(:only-child)': {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          },
          '&:not(:first-of-type):not(:last-child):not(:only-child)': {
            borderRadius: 0,
            marginRight: -1, // Pulls next one over to overlap
          },
        }),
      props.isEmpty &&
        withModifier('empty', {
          backgroundColor: Colors.grey100,
        }),
      props.shadow &&
        withModifier('shadowed', {
          boxShadow: props.shadow,
          transitionDuration: Animation.transitionDuration,
          transitionProperty: 'box-shadow',
          transitionTimingFunction: Animation.transitionTimingFunction,
        }),
    ];
  },
});

/**
 * Renders the shell around an icon or number, representing one or more services.
 */
export const ServiceIconShell = (props: ServiceIconShellProps) => {
  return (
    <span css={Styles.root(props)} data-testid="ServiceIconShell">
      {props.children}
    </span>
  );
};
